<template>
  <div>
    <PageAllow controller="ReportDownloads" action="index" />
    <Breadcrumbs :items="items" />

    <v-sheet class="transparent-background-color">
      <v-row dense class="mx-5">
        <v-col cols="12" sm="8" md="4">
          <v-row dense align="center" class="pa-1">
            <SearcherKeyword
              ref="keyword"
              v-model="options.keyword"
              @text="keyword = $event"
              :label="$t('ReportDownloads.TableHeaders.reportName')"
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="4" md="8" align-self="center">
          <v-row dense align="center" class="search-controls">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="px-0 py-0 caption font-weight-bold grey--text"
                  color="primary"
                  @click.stop="onSearch()"
                >
                  <v-icon small class="pe-1">fa-search</v-icon>
                  {{ $t("Search.search") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.searchTip") }}</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                  small
                  text
                  class="caption font-weight-bold grey--text"
                  @click.stop="resetSearch()"
                >
                  <v-icon x-small class="pe-1">fa-trash-alt</v-icon>
                  {{ $t("Search.reset") }}
                </v-btn>
              </template>
              <span>{{ $t("Search.resetTip") }}</span>
            </v-tooltip>
          </v-row>
        </v-col>
      </v-row>

      <div class="mx-4 mb-4">
        <v-data-table
          :headers="headers"
          :items="reportDownloads"
          :options.sync="options"
          :page.sync="paging.page"
          :server-items-length="paging.count"
          :loading="loading"
          multi-sort
          class="elevation-2 px-4 pt-1 sticky-table"
          id="sticky-table"
          :class="stickTableCols ? 'sticky-first-col sticky-last-col' : ''"
          fixed-header
          :footer-props="$t('$config.footer-props')"
        >
          <template v-slot:header.actions="{ header }">
            {{ header.text }}
            <v-icon x-small>fa-sliders-h</v-icon>
          </template>

          <template v-slot:item.created="{ item }">
            <div
              class="caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
            >
              {{ item.created | formatTime }}
            </div>
          </template>
          <template v-slot:item.generation_time="{ item, index }">
            <div
              class="caption secondary--text"
              :class="
                $vuetify.theme.dark ? 'text--lighten-1' : 'text--darken-1'
              "
              :v-resize="!index ? $stickTableResizer() : null"
            >
              {{ item.generation_time | formatTime }}
            </div>
          </template>
          <template v-slot:item.memo="{ item }">
            <span>
              {{
                item.memo && item.memo.length > 12
                  ? item.memo.slice(0, 12) + "......"
                  : item.memo
              }}
              <v-icon
                small
                color="primary"
                class=""
                style="display: inline-block"
                @click.stop="
                  currentItem = JSON.parse(JSON.stringify(item));
                  memo_dialog = true;
                "
                >fa-edit
              </v-icon>
            </span>
          </template>
          <template v-slot:item.actions="{ item }">
            <v-btn
              v-if="item.status"
              small
              dark
              color="print"
              class="px-2 me-2"
              :href="
                item.report_url
                  ? encodeURI(item.report_url).replace('+', '%2B')
                  : ''
              "
              target="_blank"
            >
              <v-icon x-small class="pe-1">fa-file-download</v-icon>
              {{ $t("ReportDownloads.Opt.download") }}
            </v-btn>
          </template>
        </v-data-table>
      </div>
    </v-sheet>
    <v-dialog v-model="memo_dialog" max-width="480px" v-if="currentItem">
      <v-card>
        <v-card-title class="mx-3">
          <span class="d-inline-block text-truncate" style="max-width: 360px">
            {{ currentItem.report_name }}
          </span>
          <v-spacer></v-spacer>
          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" @click="memo_dialog = false">
                fa-times
              </v-icon>
            </template>
            {{ $t("$vuetify.close") }}
          </v-tooltip>
        </v-card-title>
        <v-divider />
        <v-card-text class="px-4 pt-8">
          <v-textarea
            v-model="currentItem.memo"
            outlined
            :label="$t('Order.DetailFields.memo')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="secondary" outlined @click="memo_dialog = false">
            {{ $t("$vuetify.close") }}
          </v-btn>
          <v-btn
            color="accent"
            depressed
            @click="saveMemo(currentItem.id, currentItem.memo)"
          >
            {{ $t("$vuetify.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
var intervalRef = void 0;
var notifyDisplayed = 0;
import { mapState } from "vuex";
import i18n from "@/i18n";
import PageAllow from "@/components/PageAllow";
import Breadcrumbs from "@/components/Breadcrumbs";
import SearcherKeyword from "@/components/plugins/SearcherKeyword";
import ReportDownloads from "@/api/report_downloads";

export default {
  components: {
    PageAllow,
    Breadcrumbs,
    SearcherKeyword,
  },

  computed: {
    items: () => [
      {
        text: i18n.t("Nav.home"),
        disabled: false,
        href: "/home",
      },
      {
        text: i18n.t("Nav.reportCenter"),
        disabled: false,
        href: "/report-downloads",
      },
      {
        text: i18n.t("Nav.reportDownloads"),
        disabled: true,
        href: "/report-downloads",
      },
    ],
    headers: () => [
      {
        text: i18n.t("ReportDownloads.TableHeaders.reportName"),
        align: "start",
        class: "text-no-wrap",
        sortable: false,
        value: "report_name",
      },
      {
        text: i18n.t("ReportDownloads.TableHeaders.created"),
        align: "start",
        class: "text-no-wrap",
        sortable: false,
        value: "created",
      },
      {
        text: i18n.t("ReportDownloads.TableHeaders.generationTime"),
        align: "start",
        sortable: false,
        class: "text-no-wrap",
        value: "generation_time",
      },
      {
        text: i18n.t("ReportDownloads.TableHeaders.memo"),
        align: "start",
        sortable: false,
        class: "text-no-wrap",
        value: "memo",
      },
      {
        text: i18n.t("TableHeaders.actions"),
        align: "start",
        sortable: false,
        class: "text-no-wrap",
        value: "actions",
      },
    ],
    // 使用对象展开运算符将此对象混入到外部对象中
    ...mapState(["loading", "stickTableCols"]),
  },

  mounted() {
    // 一览列表每10秒自动刷新
    this.getReportDownloadsFromAPI();
    intervalRef = setInterval(() => {
      if (!notifyDisplayed) {
        this.getReportDownloadsFromAPI();
      }
    }, 10000);
  },

  data: () => ({
    keyword: "",
    options: {
      keyword: "",
      sortBy: [],
      sortDesc: [],
    },
    paging: {},
    reportDownloads: [],
    memo_dialog: false,
    currentItem: null,
  }),

  methods: {
    resetSearch() {
      [this.$refs.keyword].forEach((k) => {
        if (k) k.clear();
      });
    },
    onSearch() {
      this.options = Object.assign({}, this.options, {
        keyword: this.keyword,
      });
    },
    getReportDownloadsFromAPI() {
      ReportDownloads.fetch(this.options).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.reportDownloads = response.data.report_download_records;
            this.paging = response.data.paging;
          }
        }.bind(this)
      );
    },
    saveMemo(id, memo) {
      ReportDownloads.saveMemo({
        id: id,
        memo: memo,
      }).then(
        function (response) {
          if (response.data.status.isOK()) {
            this.$snackbar.success(
              i18n.t("ReportDownloads.TableHeaders.saveSuccess")
            );
            this.memo_dialog = false;
            this.getReportDownloadsFromAPI();
          }
        }.bind(this)
      );
    },
  },

  watch: {
    options: {
      // immediate: true, //为避免覆盖session里保存的options，不能启用immediate
      deep: true,
      handler() {
        this.getReportDownloadsFromAPI();
      },
    },
  },

  beforeDestroy() {
    this.$store.dispatch("threadLoadingStopAll");
    clearInterval(intervalRef);
    intervalRef = void 0;
  },
};
</script>